import { useState, useCallback, useEffect, useRef } from 'react'
import nacl from 'tweetnacl'
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from 'tweetnacl-util'
import { api } from '../services/api'
import {
  DoubleRatchet,
  x3dhInitiate,
  wireEnc,
  wireDec,
  createFallbackDRState,
  hmac256,
  genKP,
  kdfRoot,
  generateIdentityKeyPair as genIK,
  generateSignedPreKey as genSPK,
  generateOneTimePreKeys as genOPKs,
  type KeyPair,
  type SessionState,
  type PreKeyBundle as CryptoPreKeyBundle,
  type DRState,
} from '../crypto/signal-protocol'
import { getSignalStore, type SignalStore } from '../crypto/signal-store'

/**
 * Signal Protocol Hook for End-to-End Encryption
 *
 * Full implementation:
 * - X3DH key agreement + Double Ratchet
 * - Forward secrecy via DH ratchet
 * - Out-of-order message handling
 * - Backward compat with legacy v4 sessions
 * - Symmetric fallback when pre-key bundle unavailable
 */

// ======================== Types (re-exported) ========================

export type { KeyPair, SessionState }
export type { SignedPreKey, OneTimePreKey, PreKeyBundle, X3DHHeader } from '../crypto/signal-protocol'

// Legacy compat types
export interface Session {
  recipientId: string
  sharedKey: Uint8Array
  sendChainKey: Uint8Array
  receiveChainKey: Uint8Array
  sendCounter: number
  receiveCounter: number
}

interface SignedPreKeyLegacy {
  keyId: number
  publicKey: Uint8Array
  signature: Uint8Array
}

interface PreKeyBundleAPI {
  identityKey: Uint8Array
  signedPreKey: Uint8Array
  signedPreKeySignature: Uint8Array
  oneTimePreKey?: Uint8Array
  oneTimePreKeyId?: number
}

// ======================== Storage Keys ========================

const STORAGE_KEYS = {
  IDENTITY_KEY: 'lynkstar_identity_key',
  SIGNED_PRE_KEY: 'lynkstar_signed_pre_key',
  SESSIONS: 'lynkstar_sessions_v4', // legacy
  MY_UUID: 'lynkstar_my_uuid',
}

// ======================== UUID Helpers ========================

const isUUID = (s: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)

const getRecipientUUID = (id: string): string => {
  if (isUUID(id)) return id
  const s = localStorage.getItem(`lynkstar_uuid_${id}`)
  return (s && isUUID(s)) ? s : id
}

let _uuid: string | null = null

const getMyUUID = (): string | null => {
  const s = localStorage.getItem(STORAGE_KEYS.MY_UUID)
  if (s && isUUID(s)) { _uuid = s; return s }
  if (_uuid && isUUID(_uuid)) return _uuid
  try {
    const u = JSON.parse(localStorage.getItem('lynkstar_user') || '{}')
    if (u.id && isUUID(u.id)) {
      _uuid = u.id
      localStorage.setItem(STORAGE_KEYS.MY_UUID, u.id)
      return u.id
    }
  } catch (e) { console.warn('[E2EE] suppressed:', e) }
  return null
}

getMyUUID()

// ======================== Legacy v4 compat ========================

interface LegSess {
  recipientId: string; sharedKey: Uint8Array
  sendChainKey: Uint8Array; receiveChainKey: Uint8Array
  sendCounter: number; receiveCounter: number
}

function loadLegacy(tid: string): LegSess | null {
  try {
    const v4 = localStorage.getItem('lynkstar_sessions_v4')
    if (!v4) return null
    const p = JSON.parse(v4); const s = p[tid]; if (!s) return null
    return {
      recipientId: tid, sharedKey: decodeBase64(s.sharedKey),
      sendChainKey: s.sendChainKey ? decodeBase64(s.sendChainKey) : decodeBase64(s.sharedKey),
      receiveChainKey: s.receiveChainKey ? decodeBase64(s.receiveChainKey) : decodeBase64(s.sharedKey),
      sendCounter: s.sendCounter || 0, receiveCounter: s.receiveCounter || 0,
    }
  } catch (e) { console.warn('[E2EE] suppressed:', e); return null }
}

function legDecrypt(data: Uint8Array, s: LegSess): string | null {
  const v = data[0]
  if (v === 2) {
    const cnt = (data[1] << 24) | (data[2] << 16) | (data[3] << 8) | data[4]
    let ck = s.receiveChainKey
    for (let i = s.receiveCounter; i < cnt; i++) {
      ck = nacl.hash(new Uint8Array([...ck, ...decodeUTF8('lynkstar:next')])).slice(0, 32)
    }
    const mk = nacl.hash(new Uint8Array([...ck, ...decodeUTF8('lynkstar:msg')])).slice(0, 32)
    const dec = nacl.secretbox.open(data.slice(29), data.slice(5, 29), mk)
    return dec ? encodeUTF8(dec) : null
  }
  const dec = nacl.secretbox.open(data.slice(24), data.slice(0, 24), s.sharedKey)
  return dec ? encodeUTF8(dec) : null
}

function legEncrypt(text: string, s: LegSess): { ct: string; ns: LegSess } {
  const mk = nacl.hash(new Uint8Array([...s.sendChainKey, ...decodeUTF8('lynkstar:msg')])).slice(0, 32)
  const nk = nacl.hash(new Uint8Array([...s.sendChainKey, ...decodeUTF8('lynkstar:next')])).slice(0, 32)
  const nonce = nacl.randomBytes(24)
  const ct = nacl.secretbox(decodeUTF8(text), nonce, mk)
  const c = s.sendCounter
  const b = new Uint8Array(1 + 4 + 24 + ct.length)
  b[0] = 2; b[1] = (c >> 24) & 0xff; b[2] = (c >> 16) & 0xff; b[3] = (c >> 8) & 0xff; b[4] = c & 0xff
  b.set(nonce, 5); b.set(ct, 29)
  return { ct: encodeBase64(b), ns: { ...s, sendChainKey: nk, sendCounter: c + 1 } }
}

function saveLegacy(s: LegSess): void {
  try {
    const all = JSON.parse(localStorage.getItem('lynkstar_sessions_v4') || '{}')
    all[s.recipientId] = {
      ...s, sharedKey: encodeBase64(s.sharedKey),
      sendChainKey: encodeBase64(s.sendChainKey), receiveChainKey: encodeBase64(s.receiveChainKey),
    }
    localStorage.setItem('lynkstar_sessions_v4', JSON.stringify(all))
  } catch (e) { console.warn('[E2EE] suppressed:', e) }
}

// ======================== Helper ========================

async function fetchTimeout<T>(p: Promise<T>, ms = 5000): Promise<T> {
  return Promise.race([p, new Promise<T>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))])
}

// ======================== Main Hook ========================

export function useSignalProtocol(recipientId: string, currentUserId?: string) {
  const [hasSession, setHasSession] = useState(false)
  const [identityKeyPair, setIdentityKeyPair] = useState<KeyPair | null>(null)
  const [isEstablishingSession, setIsEstablishingSession] = useState(false)
  const sessionPromiseRef = useRef<Promise<SessionState> | null>(null)
  const storeRef = useRef<SignalStore | null>(null)

  // Initialize
  useEffect(() => {
    const init = async () => {
      const store = await getSignalStore()
      storeRef.current = store

      let kp = await store.loadIdentityKeyPair()
      if (!kp) {
        // Try loading from legacy localStorage
        try {
          const raw = localStorage.getItem(STORAGE_KEYS.IDENTITY_KEY)
          if (raw) {
            const p = JSON.parse(raw)
            kp = { publicKey: decodeBase64(p.publicKey), secretKey: decodeBase64(p.secretKey) }
            await store.saveIdentityKeyPair(kp)
          }
        } catch (e) { console.warn('[E2EE] suppressed:', e) }
      }

      if (!kp) {
        kp = genIK()
        await store.saveIdentityKeyPair(kp)
        // Also save to legacy location for compat
        localStorage.setItem(STORAGE_KEYS.IDENTITY_KEY, JSON.stringify({
          publicKey: encodeBase64(kp.publicKey), secretKey: encodeBase64(kp.secretKey),
        }))
      }

      setIdentityKeyPair(kp)

      // Check for existing v5 session
      const sess = await store.loadSession(recipientId)
      if (sess) {
        setHasSession(true)
      } else {
        // Check legacy
        const leg = loadLegacy(recipientId)
        setHasSession(!!leg)
      }
    }
    init()
  }, [recipientId])

  // Generate identity key pair
  const generateIdentityKeyPair = useCallback((): KeyPair => {
    const kp = genIK()
    localStorage.setItem(STORAGE_KEYS.IDENTITY_KEY, JSON.stringify({
      publicKey: encodeBase64(kp.publicKey), secretKey: encodeBase64(kp.secretKey),
    }))
    setIdentityKeyPair(kp)
    if (storeRef.current) storeRef.current.saveIdentityKeyPair(kp)
    return kp
  }, [])

  // Generate signed pre-key
  const generateSignedPreKey = useCallback((keyId: number): SignedPreKeyLegacy => {
    if (!identityKeyPair) throw new Error('Identity key not initialized')
    const spk = genSPK(identityKeyPair.secretKey, keyId)
    if (storeRef.current) storeRef.current.saveSignedPreKey(spk)
    // Also save to legacy location
    localStorage.setItem(`${STORAGE_KEYS.SIGNED_PRE_KEY}_${keyId}`, JSON.stringify({
      keyId, publicKey: encodeBase64(spk.keyPair.publicKey),
      secretKey: encodeBase64(spk.keyPair.secretKey), signature: encodeBase64(spk.signature),
    }))
    return { keyId, publicKey: spk.keyPair.publicKey, signature: spk.signature }
  }, [identityKeyPair])

  // Generate one-time pre-keys
  const generateOneTimePreKeys = useCallback((startId: number, count: number): { keyId: number; publicKey: Uint8Array }[] => {
    const opks = genOPKs(startId, count)
    for (const opk of opks) {
      if (storeRef.current) storeRef.current.saveOneTimePreKey(opk)
      localStorage.setItem(`lynkstar_otpk_${opk.keyId}`, JSON.stringify({
        keyId: opk.keyId, publicKey: encodeBase64(opk.keyPair.publicKey),
        secretKey: encodeBase64(opk.keyPair.secretKey),
      }))
    }
    return opks.map(o => ({ keyId: o.keyId, publicKey: o.keyPair.publicKey }))
  }, [])

  // Perform X3DH
  const performX3DH = useCallback(async (bundle: PreKeyBundleAPI): Promise<Uint8Array> => {
    if (!identityKeyPair) throw new Error('Identity key not initialized')
    const cryptoBundle: CryptoPreKeyBundle = {
      identityKey: bundle.identityKey,
      signedPreKey: bundle.signedPreKey,
      signedPreKeyId: 1,
      signedPreKeySignature: bundle.signedPreKeySignature,
      oneTimePreKey: bundle.oneTimePreKey,
      oneTimePreKeyId: bundle.oneTimePreKeyId,
    }
    const result = x3dhInitiate(identityKeyPair, cryptoBundle)
    return result.sk
  }, [identityKeyPair])

  // Create session (legacy compat wrapper)
  const createSession = useCallback((sharedKey: Uint8Array, myPub?: string, theirPub?: string) => {
    const deriveChainKeys = (sk: Uint8Array, a: string, b: string) => {
      const isLower = a < b
      const cA = nacl.hash(new Uint8Array([...sk, ...decodeUTF8('lynkstar:chain:A')])).slice(0, 32)
      const cB = nacl.hash(new Uint8Array([...sk, ...decodeUTF8('lynkstar:chain:B')])).slice(0, 32)
      return { sendChainKey: isLower ? cA : cB, receiveChainKey: isLower ? cB : cA }
    }

    let sendCK: Uint8Array, recvCK: Uint8Array
    if (myPub && theirPub) {
      const c = deriveChainKeys(sharedKey, myPub, theirPub)
      sendCK = c.sendChainKey; recvCK = c.receiveChainKey
    } else {
      sendCK = nacl.hash(new Uint8Array([...sharedKey, ...decodeUTF8('lynkstar:send')])).slice(0, 32)
      recvCK = nacl.hash(new Uint8Array([...sharedKey, ...decodeUTF8('lynkstar:recv')])).slice(0, 32)
    }

    const session: LegSess = {
      recipientId, sharedKey, sendChainKey: sendCK, receiveChainKey: recvCK,
      sendCounter: 0, receiveCounter: 0,
    }
    saveLegacy(session)
    setHasSession(true)
    return session
  }, [recipientId])

  // Establish session with Double Ratchet (v5) or fallback
  const establishSession = useCallback(async (): Promise<SessionState> => {
    const store = storeRef.current || await getSignalStore()

    // Check v5 first
    const existing = await store.loadSession(recipientId)
    if (existing) return existing

    // If already establishing, wait
    if (sessionPromiseRef.current) return sessionPromiseRef.current

    const myIK = await store.loadIdentityKeyPair()
    if (!myIK) throw new Error('Identity key not initialized')

    setIsEstablishingSession(true)

    const promise = (async (): Promise<SessionState> => {
      try {
        console.debug('[E2EE v5] Establishing session with:', recipientId)

        // Try to fetch pre-key bundle for proper X3DH
        let theirIK: Uint8Array | null = null
        let bundleData: { signedPreKey: Uint8Array; signedPreKeyId: number; signedPreKeySignature: Uint8Array; oneTimePreKey?: Uint8Array; oneTimePreKeyId?: number } | null = null

        try {
          const raw = await fetchTimeout(api.getPreKeyBundle(recipientId), 5000)
          const ikStr = raw.identityKey || (raw as unknown as Record<string, string>).identity_key
          if (ikStr) {
            theirIK = decodeBase64(ikStr as string)
            const spkStr = raw.signedPreKey || (raw as unknown as Record<string, string>).signed_pre_key
            if (spkStr) {
              bundleData = {
                signedPreKey: decodeBase64(spkStr as string),
                signedPreKeyId: raw.signedPreKeyId || (raw as unknown as Record<string, number>).signed_pre_key_id || 1,
                signedPreKeySignature: decodeBase64(
                  (raw.signedPreKeySignature || (raw as unknown as Record<string, string>).signed_pre_key_sig || '') as string
                ),
                oneTimePreKey: raw.oneTimePreKey ? decodeBase64(raw.oneTimePreKey as string) : undefined,
                oneTimePreKeyId: raw.oneTimePreKeyId as number | undefined,
              }
            }
          }
        } catch (e) {
          console.debug('[E2EE v5] PreKeyBundle fetch failed:', e)
        }

        // Fallback: try user info
        if (!theirIK) {
          try {
            const uinfo = await fetchTimeout(api.getUserByUsername(recipientId), 5000)
            const pk = uinfo.publicKey || (uinfo as unknown as Record<string, string>).identity_key
            if (pk) theirIK = decodeBase64(pk)
            if (uinfo.id && isUUID(uinfo.id) && uinfo.id !== recipientId) {
              localStorage.setItem(`lynkstar_uuid_${recipientId}`, uinfo.id)
            }
          } catch (e) { console.warn('[E2EE] suppressed:', e) }
        }

        let drState: DRState

        if (theirIK && bundleData) {
          // Full X3DH + Double Ratchet
          console.debug('[E2EE v5] X3DH with pre-key bundle')
          const bundle: CryptoPreKeyBundle = {
            identityKey: theirIK,
            ...bundleData,
          }
          const { sk } = x3dhInitiate(myIK, bundle)
          // Alice role: use signed pre-key as Bob's initial ratchet key
          drState = DoubleRatchet.alice(sk, bundle.signedPreKey).s
          await store.saveRemoteIdentity(recipientId, theirIK)
        } else if (theirIK) {
          // DH-based shared secret (no pre-key bundle available)
          console.debug('[E2EE v5] DH-based session (no bundle)')
          const dhResult = nacl.scalarMult(myIK.secretKey, theirIK)
          const myPub = encodeBase64(myIK.publicKey)
          const theirPub = encodeBase64(theirIK)
          const sorted = [myPub, theirPub].sort()
          const ctx = decodeUTF8('lynkstar:e2ee:' + sorted[0] + ':' + sorted[1])
          const combined = new Uint8Array(dhResult.length + ctx.length)
          combined.set(dhResult, 0); combined.set(ctx, dhResult.length)
          const sk = nacl.hash(combined).slice(0, 32)

          // Use fallback DR state but with DH-derived key
          const kp = genKP()
          const isFirst = sorted[0] === myPub
          const simDH = hmac256(sk, new TextEncoder().encode('DH_SIM'))
          const { rk, ck } = kdfRoot(sk, simDH)
          drState = {
            dhSend: kp, dhRemote: null, rk,
            sCK: isFirst ? ck : sk.slice(0, 32),
            rCK: isFirst ? sk.slice(0, 32) : ck,
            sN: 0, rN: 0, pN: 0, mk: {},
          }

          await store.saveRemoteIdentity(recipientId, theirIK)

          // Also create legacy session for backward compat
          createSession(sk, myPub, theirPub)
        } else {
          // Symmetric fallback with UUIDs
          console.debug('[E2EE v5] Symmetric fallback session')
          let myId = getMyUUID()
          if (!myId && currentUserId && isUUID(currentUserId)) {
            myId = currentUserId
            localStorage.setItem(STORAGE_KEYS.MY_UUID, currentUserId)
            _uuid = currentUserId
          }
          if (!myId) {
            try {
              const me = await fetchTimeout(api.getCurrentUser(), 3000)
              if (me.id && isUUID(me.id)) {
                myId = me.id; _uuid = me.id
                localStorage.setItem(STORAGE_KEYS.MY_UUID, me.id)
              }
            } catch (e) { console.warn('[E2EE] suppressed:', e) }
          }
          if (!myId || !isUUID(myId)) throw new Error('Cannot establish session: no UUID')

          const theirId = getRecipientUUID(recipientId)
          if (!isUUID(theirId)) throw new Error('Cannot establish session: recipient UUID unavailable')

          drState = createFallbackDRState(myId, theirId)

          // Also create legacy v4 session for backward compat
          const sorted = [myId, theirId].sort()
          const keyMaterial = 'lynkstar:v4:' + sorted[0] + ':' + sorted[1]
          const localKey = nacl.hash(decodeUTF8(keyMaterial)).slice(0, 32)
          createSession(localKey, myId, theirId)
        }

        const sess: SessionState = {
          rid: recipientId,
          dr: drState,
          x3dh: !!bundleData,
          ts: Date.now(),
          lu: Date.now(),
        }

        await store.saveSession(recipientId, sess)
        setHasSession(true)
        console.debug('[E2EE v5] Session established')
        return sess
      } catch (error) {
        console.error('[E2EE v5] Session establishment failed:', error)

        // Last resort fallback
        const myId = getMyUUID() || currentUserId
        const theirId = getRecipientUUID(recipientId)
        if (myId && isUUID(myId) && isUUID(theirId)) {
          const drState = createFallbackDRState(myId, theirId)
          const sess: SessionState = {
            rid: recipientId, dr: drState, x3dh: false,
            ts: Date.now(), lu: Date.now(),
          }
          await store.saveSession(recipientId, sess)
          setHasSession(true)

          // Legacy compat
          const sorted = [myId, theirId].sort()
          const lk = nacl.hash(decodeUTF8('lynkstar:v4:' + sorted[0] + ':' + sorted[1])).slice(0, 32)
          createSession(lk, myId, theirId)

          return sess
        }
        throw error
      } finally {
        setIsEstablishingSession(false)
        sessionPromiseRef.current = null
      }
    })()

    sessionPromiseRef.current = promise
    return promise
  }, [recipientId, createSession, currentUserId])

  // Encrypt message
  const encrypt = useCallback(async (plaintext: string): Promise<string> => {
    const store = storeRef.current || await getSignalStore()

    // Try v5 session first
    let sess = await store.loadSession(recipientId)
    if (!sess) {
      try {
        sess = await establishSession()
      } catch (e) {
        console.error('[E2EE] v5 session failed, trying legacy:', e)
      }
    }

    if (sess) {
      // Double Ratchet encrypt
      const ratchet = new DoubleRatchet(sess.dr)
      try {
        const { hdr, ct, nonce } = ratchet.encrypt(decodeUTF8(plaintext))
        sess.dr = ratchet.s
        sess.lu = Date.now()
        await store.saveSession(recipientId, sess)
        return encodeBase64(wireEnc(hdr, nonce, ct))
      } catch (e) {
        console.error('[E2EE] DR encrypt failed, falling back to legacy:', e)
      }
    }

    // Legacy fallback
    let leg = loadLegacy(recipientId)
    if (!leg) {
      // Force establish which creates legacy session too
      await establishSession()
      leg = loadLegacy(recipientId)
    }
    if (!leg) throw new Error('Could not establish any session')

    const { ct, ns } = legEncrypt(plaintext, leg)
    saveLegacy(ns)
    return ct
  }, [recipientId, establishSession])

  // Decrypt message (async)
  const decryptAsync = useCallback(async (encryptedData: string): Promise<string> => {
    if (encryptedData.startsWith('PLAIN:')) {
      try { return encodeUTF8(decodeBase64(encryptedData.slice(6))) } catch (e) { console.warn('[E2EE] base64 decode failed:', e); return encryptedData }
    }

    const store = storeRef.current || await getSignalStore()
    const data = decodeBase64(encryptedData)

    // Try v5 Double Ratchet (version byte = 3)
    const wireMsg = wireDec(data)
    if (wireMsg) {
      let sess = await store.loadSession(recipientId)
      if (!sess) {
        try { sess = await establishSession() } catch (e) { console.warn('[E2EE] suppressed:', e) }
      }
      if (sess) {
        const ratchet = new DoubleRatchet(sess.dr)
        const pt = ratchet.decrypt(wireMsg.hdr, wireMsg.ct, wireMsg.nonce)
        if (pt) {
          sess.dr = ratchet.s
          sess.lu = Date.now()
          await store.saveSession(recipientId, sess)
          return encodeUTF8(pt)
        }
      }
    }

    // Try legacy decrypt
    let leg = loadLegacy(recipientId)
    if (!leg) {
      try { await establishSession(); leg = loadLegacy(recipientId) } catch (e) { console.warn('[E2EE] suppressed:', e) }
    }
    if (leg) {
      const result = legDecrypt(data, leg)
      if (result !== null) {
        // Update legacy session counter
        const v = data[0]
        if (v === 2) {
          const cnt = (data[1] << 24) | (data[2] << 16) | (data[3] << 8) | data[4]
          let ck = leg.receiveChainKey
          for (let i = leg.receiveCounter; i <= cnt; i++) {
            ck = nacl.hash(new Uint8Array([...ck, ...decodeUTF8('lynkstar:next')])).slice(0, 32)
          }
          leg.receiveChainKey = ck
          leg.receiveCounter = cnt + 1
        } else {
          leg.receiveCounter++
        }
        saveLegacy(leg)
        return result
      }
    }

    return '[Encrypted message - decryption failed]'
  }, [recipientId, establishSession])

  // Decrypt message (sync - legacy compat)
  const decrypt = useCallback((encryptedData: string): string => {
    if (encryptedData.startsWith('PLAIN:')) {
      try { return encodeUTF8(decodeBase64(encryptedData.slice(6))) } catch (e) { console.warn('[E2EE] base64 decode failed:', e); return encryptedData }
    }

    const leg = loadLegacy(recipientId)
    if (!leg) return '[Encrypted message - session unavailable]'

    try {
      const data = decodeBase64(encryptedData)
      const result = legDecrypt(data, leg)
      if (result === null) return '[Encrypted message - decryption failed]'

      // Update counter
      const v = data[0]
      if (v === 2) {
        const cnt = (data[1] << 24) | (data[2] << 16) | (data[3] << 8) | data[4]
        let ck = leg.receiveChainKey
        for (let i = leg.receiveCounter; i <= cnt; i++) {
          ck = nacl.hash(new Uint8Array([...ck, ...decodeUTF8('lynkstar:next')])).slice(0, 32)
        }
        leg.receiveChainKey = ck; leg.receiveCounter = cnt + 1
      } else {
        leg.receiveCounter++
      }
      saveLegacy(leg)
      return result
    } catch (e) {
      console.error('Decryption error:', e)
      return '[Encrypted message - decryption error]'
    }
  }, [recipientId])

  // Get public identity key
  const getPublicIdentityKey = useCallback((): string | null => {
    if (!identityKeyPair) return null
    return encodeBase64(identityKeyPair.publicKey)
  }, [identityKeyPair])

  // Verify pre-key signature
  const verifyPreKeySignature = useCallback((
    identityKey: Uint8Array, preKey: Uint8Array, signature: Uint8Array
  ): boolean => {
    try {
      const signKP = nacl.sign.keyPair.fromSeed(identityKey.slice(0, 32))
      return nacl.sign.detached.verify(preKey, signature, signKP.publicKey)
    } catch (e) { console.warn('[E2EE] suppressed:', e); return false }
  }, [])

  // Clear session
  const clearSession = useCallback(async () => {
    // Clear v5
    const store = storeRef.current || await getSignalStore()
    await store.deleteSession(recipientId)

    // Clear legacy
    try {
      const all = JSON.parse(localStorage.getItem('lynkstar_sessions_v4') || '{}')
      delete all[recipientId]
      localStorage.setItem('lynkstar_sessions_v4', JSON.stringify(all))
    } catch (e) { console.warn('[E2EE] suppressed:', e) }

    setHasSession(false)
  }, [recipientId])

  return {
    hasSession,
    isEstablishingSession,
    identityKeyPair,
    generateIdentityKeyPair,
    generateSignedPreKey,
    generateOneTimePreKeys,
    performX3DH,
    createSession,
    establishSession,
    encrypt,
    decrypt,
    decryptAsync,
    getPublicIdentityKey,
    verifyPreKeySignature,
    clearSession,
  }
}

/**
 * Standalone key generation for registration (no hook required)
 */
export function generateRegistrationKeys() {
  const identityKeyPair = nacl.box.keyPair()

  // Store identity key
  localStorage.setItem(STORAGE_KEYS.IDENTITY_KEY, JSON.stringify({
    publicKey: encodeBase64(identityKeyPair.publicKey),
    secretKey: encodeBase64(identityKeyPair.secretKey),
  }))

  // Generate signed pre-key (key ID = 1)
  const signedPreKeyPair = nacl.box.keyPair()
  const signKeyPair = nacl.sign.keyPair.fromSeed(identityKeyPair.secretKey.slice(0, 32))
  const signedPreKeySignature = nacl.sign.detached(signedPreKeyPair.publicKey, signKeyPair.secretKey)

  localStorage.setItem(`${STORAGE_KEYS.SIGNED_PRE_KEY}_1`, JSON.stringify({
    keyId: 1,
    publicKey: encodeBase64(signedPreKeyPair.publicKey),
    secretKey: encodeBase64(signedPreKeyPair.secretKey),
    signature: encodeBase64(signedPreKeySignature),
  }))

  // Generate one-time pre-keys (10 keys starting from ID 1)
  const oneTimePreKeys: { id: number; publicKey: string }[] = []
  for (let i = 1; i <= 10; i++) {
    const keyPair = nacl.box.keyPair()
    localStorage.setItem(`lynkstar_otpk_${i}`, JSON.stringify({
      keyId: i,
      publicKey: encodeBase64(keyPair.publicKey),
      secretKey: encodeBase64(keyPair.secretKey),
    }))
    oneTimePreKeys.push({ id: i, publicKey: encodeBase64(keyPair.publicKey) })
  }

  // Also save to IndexedDB store async
  getSignalStore().then(async (store) => {
    await store.saveIdentityKeyPair(identityKeyPair)
    await store.saveSignedPreKey({
      keyId: 1,
      keyPair: signedPreKeyPair,
      signature: signedPreKeySignature,
    })
    for (let i = 1; i <= 10; i++) {
      const raw = localStorage.getItem(`lynkstar_otpk_${i}`)
      if (raw) {
        const p = JSON.parse(raw)
        await store.saveOneTimePreKey({
          keyId: i,
          keyPair: { publicKey: decodeBase64(p.publicKey), secretKey: decodeBase64(p.secretKey) },
        })
      }
    }
  }).catch(() => { /* ignore */ })

  return {
    identityKey: encodeBase64(identityKeyPair.publicKey),
    signedPreKey: {
      id: 1,
      publicKey: encodeBase64(signedPreKeyPair.publicKey),
      signature: encodeBase64(signedPreKeySignature),
    },
    oneTimePreKeys,
  }
}